import { useTranslation } from 'react-i18next';
import { Activity, Clock, Server, Bot, TriangleAlert } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/shared/lib/utils';
import {
  useSystemHealth,
  useKillSwitchStatus,
  useExchangeCBList,
  useExchangesHealth,
} from '@/api/hooks/use-system';
import { useBotsFleet } from '@/api/hooks/use-bots';
import { useLatencyP50 } from '@/shared/hooks/use-latency-p50';

const APP_VERSION = __APP_VERSION__;
const APP_COMMIT = __APP_COMMIT__;

/**
 * Persistent ops-health bar (LOK-1097, spec docs/design/app-shell.md § Zone C).
 * Renders inside the AppLayout grid as `grid-area: status` — never `position:fixed`.
 *
 * Fields, left → right:
 *   1. Connection state (sync/connected/disconnected) — from systemHealth query state
 *   2. Latency p50 (rolling 60s window) — from axios interceptor ring buffer
 *   3. Last sync, UTC-suffixed
 *   4. Active venues count (HEALTHY)
 *   5. Active bots count (RUNNING)
 *   6. Flagged alerts count (kill switch + non-CLOSED CBs); hidden when 0
 *   7. App env pill (deployment env: dev/staging/production)
 *   8. Build version + short SHA
 */
export function StatusBar() {
  const { t } = useTranslation('common');
  const { data: health, dataUpdatedAt, isError, isFetching } = useSystemHealth();
  const { data: bots } = useBotsFleet();
  const { data: exchangesHealth } = useExchangesHealth();
  const { data: killSwitch } = useKillSwitchStatus();
  const { data: cbList } = useExchangeCBList();
  const latencyMs = useLatencyP50();

  const env = import.meta.env.VITE_APP_ENV ?? 'development';
  const isConnected = !!health && !isError;

  const lastSync = dataUpdatedAt
    ? formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true })
    : t('statusBar.never');

  const activeVenues = (exchangesHealth?.exchanges ?? []).filter(
    (ex) => ex.state === 'HEALTHY',
  ).length;

  const runningBotIds = new Set(
    (health?.bots ?? []).filter((b) => b.runtime_state === 'running').map((b) => b.bot_id),
  );
  const activeBots = (bots?.items ?? []).filter((b) => runningBotIds.has(b.id)).length;

  const trippedCBs = (cbList?.data ?? []).filter(
    (cb) => cb.status !== 'CLOSED' && cb.status !== 'closed',
  ).length;
  const flaggedAlerts = (killSwitch?.active ? 1 : 0) + trippedCBs;

  const connectionLabel = isFetching
    ? t('statusBar.syncing')
    : isConnected
      ? t('statusBar.connected')
      : t('statusBar.disconnected');

  return (
    <footer
      className="flex items-center justify-between border-t border-border bg-white/[0.02] px-4 text-[10.5px] text-text-55 select-none h-full font-sans"
      aria-label={`Connection status: ${connectionLabel}, last sync ${lastSync}`}
    >
      {/* Left: ops health */}
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex items-center gap-1.5">
          <Activity
            className={cn(
              'h-3 w-3',
              isFetching && 'text-info-text animate-pulse',
              !isFetching && isConnected && 'text-ok-text',
              !isFetching && !isConnected && 'text-err-text',
            )}
            strokeWidth={1.8}
          />
          {connectionLabel}
        </span>

        {latencyMs !== null && <span className="font-mono">{Math.round(latencyMs)}ms</span>}

        <span className="flex items-center gap-1.5">
          <Clock className="h-3 w-3" strokeWidth={1.8} />
          {lastSync} · {t('statusBar.utcHint')}
        </span>

        <span className="flex items-center gap-1.5">
          <Server className="h-3 w-3" strokeWidth={1.8} />
          <span className="font-mono">{t('statusBar.activeVenues', { count: activeVenues })}</span>
        </span>

        <span className="flex items-center gap-1.5">
          <Bot className="h-3 w-3" strokeWidth={1.8} />
          <span className="font-mono">{t('statusBar.activeBots', { count: activeBots })}</span>
        </span>

        {flaggedAlerts > 0 && (
          <span className="flex items-center gap-1.5 text-err-text">
            <TriangleAlert className="h-3 w-3" strokeWidth={1.8} />
            <span className="font-mono">
              {t('statusBar.flaggedAlerts', { count: flaggedAlerts })}
            </span>
          </span>
        )}
      </div>

      {/* Right: build provenance */}
      <div className="flex items-center gap-3 shrink-0">
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase font-mono',
            env === 'production'
              ? 'bg-ok/10 text-ok-text'
              : env === 'staging'
                ? 'bg-warn/10 text-warn'
                : 'bg-info/10 text-info-text',
          )}
        >
          {env}
        </span>
        <span className="text-text-40 font-mono">
          v{APP_VERSION} {APP_COMMIT}
        </span>
      </div>
    </footer>
  );
}
